const FX_PREFIX = "joylo-attn-fx:";
const FX_TTL_MS = 24 * 60 * 60 * 1000;
const MUTED_KEY = "joylo-attn-muted";

/**
 * Cross-tab guard so a toast/chime fires in one tab only. All tabs still update their
 * bell/title/favicon; only the first to claim the event plays the effect.
 */
export function claimEffect(eventKey: string): boolean {
  try {
    const key = FX_PREFIX + eventKey;
    if (localStorage.getItem(key)) return false;
    localStorage.setItem(key, String(Date.now()));
    pruneClaims();
    return true;
  } catch {
    // Storage blocked (private mode): better to double-play than to stay silent
    return true;
  }
}

function pruneClaims() {
  const cutoff = Date.now() - FX_TTL_MS;
  for (let i = localStorage.length - 1; i >= 0; i--) {
    const key = localStorage.key(i);
    if (key?.startsWith(FX_PREFIX) && Number(localStorage.getItem(key)) < cutoff) localStorage.removeItem(key);
  }
}

export function readMuted(): boolean {
  try {
    return localStorage.getItem(MUTED_KEY) === "1";
  } catch {
    return false;
  }
}

export function writeMuted(muted: boolean) {
  try {
    if (muted) localStorage.setItem(MUTED_KEY, "1");
    else localStorage.removeItem(MUTED_KEY);
  } catch {
    // Preference just won't persist
  }
}

// Peak volume (0–1); two overlapping notes stay under clipping at this level
const CHIME_PEAK_GAIN = 0.45;

let audioCtx: AudioContext | null = null;

/** Short two-note chime synthesised with Web Audio, so there is no asset to load. */
export function playChime(kind: "input" | "completed") {
  try {
    const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return;
    audioCtx = audioCtx ?? new Ctor();
    const ctx = audioCtx;
    if (ctx.state === "suspended") void ctx.resume();
    const notes = kind === "input" ? [660, 880] : [523.25, 783.99];
    notes.forEach((freq, i) => {
      const start = ctx.currentTime + i * 0.16;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      // Triangle carries more harmonics than sine, so it reads as louder at the same peak without sounding harsh
      osc.type = "triangle";
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.exponentialRampToValueAtTime(CHIME_PEAK_GAIN, start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.45);
      osc.connect(gain).connect(ctx.destination);
      osc.start(start);
      osc.stop(start + 0.5);
    });
  } catch {
    // Autoplay policy or no audio device — visual indicators still work
  }
}
